import type { TooltipContent, TooltipTrigger } from '@cobliteam/colib-ui/Tooltip';
import { type ComponentProps, cloneElement, type HTMLAttributes, type ReactElement, type ReactNode, type Ref, useRef, useState } from 'react';
import type TooltipOnIntentPopup from './TooltipOnIntentPopup';

type TriggerElementProps = HTMLAttributes<HTMLElement> & { ref?: Ref<HTMLElement> };

export interface TooltipOnIntentProps {
  render: ReactElement<TriggerElementProps>;
  children: ReactNode;
  disabled?: boolean;
  triggerProps?: Omit<ComponentProps<typeof TooltipTrigger>, 'render' | 'children'>;
  contentProps?: Omit<ComponentProps<typeof TooltipContent>, 'children'>;
}

export default function TooltipOnIntent({ render, children, disabled, triggerProps, contentProps }: TooltipOnIntentProps) {
  const [Popup, setPopup] = useState<typeof TooltipOnIntentPopup | null>(null);
  const [open, setOpen] = useState(false);
  const pending = useRef(false);
  const resolved = useRef<typeof TooltipOnIntentPopup | null>(null);
  const pressed = useRef(false);
  const releaseListener = useRef<(() => void) | null>(null);
  const releaseTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const elementRef = useRef<HTMLElement | null>(null);
  const focusPath = useRef<number[] | null>(null);
  const originalRef = render.props.ref;

  const installPopup = () => {
    if (pressed.current || !resolved.current) return;
    const root = elementRef.current;
    let current = document.activeElement;
    if (root && current && root.contains(current)) {
      const path: number[] = [];
      while (current !== root && current.parentElement) {
        path.unshift(Array.from(current.parentElement.children).indexOf(current));
        current = current.parentElement;
      }
      focusPath.current = path;
    }
    const Component = resolved.current;
    resolved.current = null;
    setPopup(() => Component);
  };

  const requestTooltip = () => {
    if (disabled) return;
    setOpen(true);
    if (pending.current) return;
    pending.current = true;
    const load = import('./TooltipOnIntentPopup');
    const delay = triggerProps?.delay ?? 0;
    const ready = delay > 0 ? Promise.all([load, new Promise<void>((resolve) => setTimeout(resolve, delay))]).then(([module]) => module) : load;
    void ready
      .then(({ default: Component }) => {
        resolved.current = Component;
        installPopup();
      })
      .catch(() => {
        pending.current = false;
      });
  };

  const trigger = cloneElement(render, {
    ref: (node: HTMLElement | null) => {
      elementRef.current = node;
      const cleanup = typeof originalRef === 'function' ? originalRef(node) : undefined;
      if (originalRef && typeof originalRef !== 'function') originalRef.current = node;
      if (node && focusPath.current) {
        const target = focusPath.current.reduce<Element | undefined>((parent, index) => parent?.children[index], node);
        focusPath.current = null;
        if (target instanceof HTMLElement) target.focus({ preventScroll: true });
      }
      return () => {
        elementRef.current = null;
        clearTimeout(releaseTimer.current);
        if (releaseListener.current) {
          window.removeEventListener('pointerup', releaseListener.current);
          window.removeEventListener('pointercancel', releaseListener.current);
        }
        if (typeof cleanup === 'function') cleanup();
        else if (typeof originalRef === 'function') originalRef(null);
        else if (originalRef) originalRef.current = null;
      };
    },
    ...(!Popup && {
      onPointerEnter: (event) => {
        render.props.onPointerEnter?.(event);
        if (!event.defaultPrevented && event.pointerType !== 'touch') requestTooltip();
      },
      onPointerLeave: (event) => {
        render.props.onPointerLeave?.(event);
        setOpen(false);
      },
      onPointerDownCapture: (event) => {
        render.props.onPointerDownCapture?.(event);
        pressed.current = true;
        const release = () => {
          pressed.current = false;
          window.removeEventListener('pointerup', release);
          window.removeEventListener('pointercancel', release);
          releaseListener.current = null;
          // Replacing the trigger between pointerdown and click cancels the original action.
          releaseTimer.current = setTimeout(installPopup, 0);
        };
        releaseListener.current = release;
        window.addEventListener('pointerup', release);
        window.addEventListener('pointercancel', release);
      },
      onFocus: (event) => {
        render.props.onFocus?.(event);
        if (event.target instanceof HTMLElement && event.target.matches(':focus-visible')) requestTooltip();
      },
      onBlur: (event) => {
        render.props.onBlur?.(event);
        setOpen(false);
      },
    }),
  });

  if (!Popup) return trigger;

  return (
    <Popup render={trigger} open={open} onOpenChange={setOpen} disabled={disabled} triggerProps={triggerProps} contentProps={contentProps}>
      {children}
    </Popup>
  );
}
